import React from 'react';
import { formatCurrencyES, formatPercentageES, formatPeriodES } from '../../../config/reportTranslations';
import './Cuadro05.css';

/**
 * Cuadro 05: Investment Portfolio Table
 *
 * Displays liquidity portfolio composition:
 * - Fondos Disponibles (Available Funds)
 * - Inversiones (Investments)
 * - Total Activos Líquidos (Total Liquid Assets)
 * - Percentage of Total Assets
 */

export interface InvestmentData {
  period: string;                    // YYYY-MM format
  fondos_disponibles: number;        // Cash and equivalents
  inversiones: number;               // Investments
  total_activos_liquidos: number;    // Total liquid assets
  activo_total: number;              // Total assets (for percentage calculation)
}

export interface Cuadro05Props {
  data: InvestmentData[];
  title?: string;
}

export const Cuadro05: React.FC<Cuadro05Props> = ({
  data,
  title = 'Cuadro 05: Portafolio de inversiones'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="cuadro-05-container">
        <h3 className="cuadro-05-title">{title}</h3>
        <p className="cuadro-05-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (newest first)
  const sortedData = [...data].sort((a, b) => b.period.localeCompare(a.period));

  // Convert to millions
  const toMillions = (value: number): number => value / 1_000_000;

  // Calculate percentage of total assets
  const calculatePercentage = (liquidAssets: number, totalAssets: number): number => {
    return totalAssets > 0 ? (liquidAssets / totalAssets) * 100 : 0;
  };

  return (
    <div className="cuadro-05-container">
      <h3 className="cuadro-05-title">{title}</h3>

      <div className="cuadro-05-table-wrapper">
        <table className="cuadro-05-table">
          <thead>
            <tr>
              <th className="cuadro-05-header-concept">Concepto</th>
              {sortedData.map(row => (
                <th key={row.period} className="cuadro-05-header-period">
                  {formatPeriodES(row.period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Fondos Disponibles */}
            <tr>
              <td className="cuadro-05-cell-concept">Fondos Disponibles</td>
              {sortedData.map(row => (
                <td key={`fondos-${row.period}`} className="cuadro-05-cell-numeric">
                  {formatCurrencyES(toMillions(row.fondos_disponibles))}
                </td>
              ))}
            </tr>

            {/* Inversiones */}
            <tr>
              <td className="cuadro-05-cell-concept">Inversiones</td>
              {sortedData.map(row => (
                <td key={`inversiones-${row.period}`} className="cuadro-05-cell-numeric">
                  {formatCurrencyES(toMillions(row.inversiones))}
                </td>
              ))}
            </tr>

            {/* Total Activos Líquidos */}
            <tr className="cuadro-05-row-total">
              <td className="cuadro-05-cell-concept-bold">Total Activos Líquidos</td>
              {sortedData.map(row => (
                <td key={`total-${row.period}`} className="cuadro-05-cell-numeric-bold">
                  {formatCurrencyES(toMillions(row.total_activos_liquidos))}
                </td>
              ))}
            </tr>

            {/* Percentage of Total Assets */}
            <tr className="cuadro-05-row-percentage">
              <td className="cuadro-05-cell-concept-italic">% del Activo Total</td>
              {sortedData.map(row => (
                <td key={`pct-${row.period}`} className="cuadro-05-cell-numeric-italic">
                  {formatPercentageES(calculatePercentage(row.total_activos_liquidos, row.activo_total))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Note */}
      <p className="cuadro-05-note">
        Nota: Los activos líquidos incluyen fondos disponibles e inversiones de corto plazo (USD millones)
      </p>

      {/* Source note */}
      <p className="cuadro-05-source">
        Fuente: Estados financieros de la institución reportados a la Superintendencia
      </p>
    </div>
  );
};

export default Cuadro05;
